/**
 * Per-product reviews modal (Judge.me).
 *
 * WHY THIS EXISTS
 * The store's only live Judge.me widget is the store-wide `all_reviews_page`
 * block, so tapping the star rating scrolled to every product's reviews at
 * once. The per-product `review_widget` block exists in the product templates
 * but is disabled. On the home SPA there is no Judge.me widget at all, and the
 * product changes without a page load, so no installed widget could track it.
 *
 * HOW
 * Judge.me's widget endpoint serves one product's reviews as HTML, needs no
 * token, and answers CORS from the storefront origin (verified against
 * emamaco.myshopify.com):
 *
 *   GET https://judge.me/reviews/reviews_for_widget
 *       ?url=<shop>&shop_domain=<shop>&platform=shopify&page=<n>&product_id=<id>
 *       &filter_rating[]=2&filter_rating[]=3&filter_rating[]=4&filter_rating[]=5
 *   -> { html, total_count, page }
 *
 * RATING FILTER — measured, not assumed. `filter_rating` takes ONE value;
 * repeating it plain keeps only the last. The ARRAY form unions correctly:
 * 5 -> 377, 4 -> 57, [4,5] -> 434. The 2..5 set below returns 473 of the
 * product's 482, i.e. its 9 one-star reviews withheld, and Judge.me
 * recomputes its own page count against the filtered set (97 -> 95 pages).
 *
 * The markup is Judge.me's own, so their stylesheet renders it. On a PDP that
 * CSS is already present; on home only the loader stub is installed, so we
 * pull it on demand through jdgm.CDN_BASE_URL — version-pinned by the app
 * embed, which is why it is read at runtime rather than hardcoded.
 *
 * The product id is read at CLICK time from #pdp-gallery[data-product-id],
 * which pdpGallerySwap keeps current — that is what makes this follow the SPA.
 */
(function () {
  var HOST = 'https://judge.me/reviews/reviews_for_widget';
  var RATINGS = [2, 3, 4, 5];   // 1-star withheld
  var cssRequested = false;
  var el = null;                // modal root, null when closed
  var state = null;             // { productId, page, total, loading }

  function shopDomain() {
    if (window.Shopify && window.Shopify.shop) return window.Shopify.shop;
    // jdgmSettings ships on every page carrying the app embed.
    if (window.jdgmSettings && window.jdgmSettings.shop_domain) return window.jdgmSettings.shop_domain;
    return location.hostname;
  }

  function currentProductId() {
    var g = document.getElementById('pdp-gallery');
    var id = g && g.getAttribute('data-product-id');
    if (id) return String(id);
    if (window.pdpMeta && window.pdpMeta.productId) return String(window.pdpMeta.productId);
    return null;
  }

  function currentProductTitle() {
    var t = document.getElementById('v2-title-name');
    if (t && t.textContent.trim()) return t.textContent.trim();
    var g = document.getElementById('pdp-gallery');
    return (g && g.getAttribute('data-product-name')) || '';
  }

  // Judge.me's widget CSS, fetched only when the modal is first opened so a
  // page that never shows reviews never pays for it.
  function ensureCss() {
    if (cssRequested) return;
    cssRequested = true;
    var base = (window.jdgm && window.jdgm.CDN_BASE_URL) || '';
    if (!base) return;                       // PDP already has it; nothing to do
    ['widget_base.css', 'widget_main.css', 'widget_media.css'].forEach(function (f) {
      var href = base + f;
      if (document.querySelector('link[href="' + href + '"]')) return;
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
    });
  }

  function fetchPage(productId, page) {
    var qs = '?url=' + encodeURIComponent(shopDomain()) +
             '&shop_domain=' + encodeURIComponent(shopDomain()) +
             '&platform=shopify&page=' + page +
             '&product_id=' + encodeURIComponent(productId) +
             RATINGS.map(function (r) { return '&filter_rating%5B%5D=' + r; }).join('');
    return fetch(HOST + qs, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  var scrollY = 0;
  function lock() {
    scrollY = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
  }
  function unlock() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  function close() {
    if (!el) return;
    el.remove();
    el = null;
    state = null;
    unlock();
    document.removeEventListener('keydown', onKey);
  }

  function onKey(e) { if (e.key === 'Escape') close(); }

  function setStatus(msg) {
    var s = el && el.querySelector('#pdp-rv-status');
    if (s) s.innerHTML = msg;
  }

  /**
   * Do the work Judge.me's widget bundle would have done to the markup.
   *
   * Review photos ship as `<img data-src=… >` with NO src AND the wrapping
   * <a> carries `jdgm--loading`, which is the class that paints the spinner —
   * setting src alone leaves a fully downloaded photo invisible at 1x1 behind
   * a ring. Timestamps ship empty with `jdgm-spinner`. We deliberately don't
   * load their bundle (keeping it off home is most of the point), so both
   * steps happen here. Idempotent: safe after their own pass on a PDP.
   */
  function hydrate(root) {
    root.querySelectorAll('img[data-src]').forEach(function (img) {
      var link = img.closest('.jdgm-rev__pic-link') || img.parentElement;
      var reveal = function () { if (link) link.classList.remove('jdgm--loading'); };
      if (!img.getAttribute('src')) {
        img.addEventListener('load', reveal);
        img.addEventListener('error', reveal);   // never strand a dead image behind a permanent spinner
        img.setAttribute('src', img.getAttribute('data-src'));
        img.loading = 'lazy';
      }
      if (img.complete && img.naturalWidth > 0) reveal();
    });
    // Dates are hidden outright. Removed rather than display:none'd so the
    // spinner class can't keep animating an invisible element, and so the two
    // surfaces can't disagree on formatting (Judge.me's own pass renders
    // 12/08/24 on a PDP where its bundle is loaded).
    root.querySelectorAll('.jdgm-rev__timestamp').forEach(function (t) { t.remove(); });
    root.querySelectorAll('.jdgm-spinner').forEach(function (e) { e.classList.remove('jdgm-spinner'); });
    // Photos lead the review. Judge.me renders them last, after title and
    // body; hoisting the container keeps their markup and styling intact.
    root.querySelectorAll('.jdgm-rev__content').forEach(function (content) {
      var pics = content.querySelector('.jdgm-rev__pics');
      if (pics && content.firstElementChild !== pics) content.insertBefore(pics, content.firstElementChild);
    });
  }

  /**
   * Load one page and REPLACE the list.
   *
   * Judge.me ships its own `.jdgm-paginate` control inside every response, and
   * those <a data-page> links are inert without their setupPaginate — that is
   * why the page buttons did nothing. Rather than hide their control and offer
   * a second one, the clicks are delegated here; because each response carries
   * a freshly rendered control, the current page marker stays correct for free.
   */
  function showPage(n) {
    if (!state || state.loading) return;
    state.loading = true;
    var body = el.querySelector('#pdp-rv-body');
    body.style.opacity = '0.45';
    fetchPage(state.productId, n).then(function (data) {
      state.loading = false;
      state.page = n;
      state.total = data.total_count || 0;
      setStatus(state.total
        ? '<strong>' + state.total + '</strong> review' + (state.total === 1 ? '' : 's') + ' for this product'
        : 'No reviews for this product yet');
      body.innerHTML = data.html || '';
      try {
        if (window.jdgm && typeof window.jdgm.customizeReviews === 'function') window.jdgm.customizeReviews(body);
        if (window.jdgm && typeof window.jdgm.setupPictures === 'function') window.jdgm.setupPictures(body);
      } catch (e) {}
      hydrate(body);
      body.style.opacity = '';
      body.scrollTop = 0;
    }).catch(function (err) {
      state.loading = false;
      body.style.opacity = '';
      setStatus('Could not load reviews right now.');
      try { console.warn('[pdp-reviews]', err); } catch (e) {}
    });
  }

  function open() {
    var productId = currentProductId();
    if (!productId) return;
    if (el) close();
    ensureCss();
    lock();

    el = document.createElement('div');
    el.id = 'pdp-reviews-modal';
    el.style.cssText = 'position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,0.5);display:flex;align-items:flex-end;justify-content:center;font-family:system-ui,-apple-system,sans-serif;';
    el.innerHTML =
      '<div style="background:#fff;width:100%;max-width:900px;max-height:88vh;display:flex;flex-direction:column;border-radius:16px 16px 0 0;overflow:hidden;">' +
        '<div style="flex:0 0 auto;display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:16px 20px;border-bottom:1px solid #e5e7eb;">' +
          '<div style="min-width:0;">' +
            '<div style="font-size:16px;font-weight:700;color:#1f2937;line-height:1.25;">' + escapeHtml(currentProductTitle()) + '</div>' +
            '<div id="pdp-rv-status" style="font-size:13px;color:#6b7280;margin-top:3px;">Loading reviews…</div>' +
          '</div>' +
          '<button type="button" id="pdp-rv-close" aria-label="Close" style="flex:0 0 auto;background:none;border:none;font-size:22px;line-height:1;cursor:pointer;color:#374151;padding:2px 4px;">✕</button>' +
        '</div>' +
        '<div id="pdp-rv-body" class="jdgm-widget jdgm-rev-widg" style="flex:1 1 auto;overflow-y:auto;padding:4px 20px 20px;-webkit-overflow-scrolling:touch;transition:opacity .12s linear;"></div>' +
      '</div>';

    el.addEventListener('click', function (e) {
      if (e.target === el) { close(); return; }
      // Judge.me's own pagination, wired to our fetch.
      var pg = e.target.closest && e.target.closest('.jdgm-paginate__page[data-page]');
      if (pg) {
        e.preventDefault();
        var n = parseInt(pg.getAttribute('data-page'), 10);
        if (n && n !== state.page) showPage(n);
      }
    });
    el.querySelector('#pdp-rv-close').addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(el);

    state = { productId: productId, page: 0, total: 0, loading: false };
    showPage(1);
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  window.openProductReviews = open;

  // The star row is rendered (twice) by emamaco-packprice and is re-created on
  // every SPA swap, so the handler is delegated rather than bound.
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('#em-scroll-to-reviews');
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    open();
  }, true);
})();
